<script setup lang="ts">
import draggable from 'vuedraggable'
import { notifyError, roundTo, emptyMongoId, toDate_wd_dd_MMMM_hh_mm } from '@/utils/helpers'
import { unitésParBoîte, getDateCoupéForCulture, culturesGérées } from '@/utils/helpers-vegco'
import type { MDBVegcoBucket } from '@/types/vegco/bucket'
import type { MDBVegcoCommandeVente, MDBVegcoCommandeVenteDétail } from '@/types/vegco/commandevente'

definePage({
  meta: {
    id: 'vgco.commandes',
    title: 'Commandes',
    icon: 'mdi-truck-trailer',
    drawerIndex: 1,
    requiresAuth: true,
    requiresSiteAccess: 'vegco'
  },
})

const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()

const selectedDay = ref(new Date(storeU.journéeSélectionnée))
const isLoading = ref(false)
const selectedCodeProduit = ref('')
const isFloaty = ref(false)
const scrollContainer = ref(null as any)

const buckets = ref<MDBVegcoBucket[]>([])
const bucketsInventaire = ref<MDBVegcoBucket[]>([])

const bucketsCommandes = ref(new Map<string, MDBVegcoBucket[]>())
const bucketsCommandesProxy = computed(() => ({
  get(id: string) : MDBVegcoBucket[] { return bucketsCommandes.value.get(id) || [] },
  set(id: string, value: MDBVegcoBucket[]) : void { bucketsCommandes.value.set(id, value) }
}));

function addToBucketsCommandes(commande: MDBVegcoCommandeVente) {
  for (const bucket of storeVegco.bucketsStore.filter((b) =>
    b.commandeVenteId == commande.id && b.culture == storeU.cultureSélectionnée.nom)) {
    if (!bucketsCommandes.value.has(commande.id)) {
       bucketsCommandes.value.set(commande.id, [])
    }

    const list = bucketsCommandes.value.get(commande.id)
    if (list == undefined) {
      continue
    }

    if (commande.statut == 'Inventaire') {
      const date = new Date(bucket.date)
      if (date.getFullYear() != selectedDay.value.getFullYear() ||
          date.getMonth() != selectedDay.value.getMonth() ||
          date.getDate() != selectedDay.value.getDate()) {
        continue
      }
    }

    // If this bucket id is not already in the list (try fix bug)
    if (list.find((b) => b.id == bucket.id) != undefined) {
      continue
    }

    list.push(bucket)
  }
}

const showNotCompletedCommandes = ref(false)
const commandesFiltered = computed(() => {
  const today = new Date(selectedDay.value)
  today.setHours(0, 0, 0, 0)

  const commandes = storeVegco.commandesStore.filter((commande) => {
    const dateMinCheck = getDateCoupéForCulture(commande, storeU.cultureSélectionnée.nom)
    dateMinCheck.setHours(dateMinCheck.getHours() - 6)
    dateMinCheck.setHours(0, 0, 0, 0)
    const dateChargé = new Date(commande.dateChargé)

    if (commande.statut != 'Inventaire' && dateChargé.getFullYear() <= 2000) {
      const dateCréation = new Date(commande.dateCréation)
      if (dateCréation.getFullYear() == today.getFullYear() &&
          dateCréation.getMonth() == today.getMonth() &&
          dateCréation.getDate() == today.getDate()) {
        return true
      }
      return false
    }

    const maCulture = commande.détails.find((détail) => détail.culture == storeU.cultureSélectionnée.nom) != undefined
    if (!maCulture) {
      return false
    }

    if (selectedDay.value >= dateMinCheck && today <= dateChargé) {
      return true
    } else if (showNotCompletedCommandes.value) {
      const isCompleted = commande.détails.every((détail) => {
        return détail.quantité == détail.quantitéConfirmée
      })

      return !isCompleted && dateChargé < today
    }
  })

  commandes.sort((a, b) => {
    if (a.dateChargé < b.dateChargé) return -1
    if (a.dateChargé > b.dateChargé) return 1
    return 0
  })

  if (storeVegco.commandeInventaire != null) {
    commandes.push(storeVegco.commandeInventaire)
  }

  return commandes
})

watch(selectedDay, (_) => {
  //selectedCodeProduit.value = ''
  getData()
})

async function onShowNotCompletedCommandesChanged() {
  storeU.createVegcoSiteSettingsIfNeeded()

  if (storeU.utilisateur.paramètresSite.vegco != null &&
    storeU.utilisateur.paramètresSite.vegco.afficherCommandesNonLivréesDuPassé != showNotCompletedCommandes.value) {
    storeU.utilisateur.paramètresSite.vegco.afficherCommandesNonLivréesDuPassé = showNotCompletedCommandes.value
    await storeU.updateParamètresSite()
  }

  await getData()
}

function selectCodeProduit(codeProduit: string) {
  if (selectedCodeProduit.value == codeProduit) {
    selectedCodeProduit.value = ''
  } else {
    selectedCodeProduit.value = codeProduit
  }
}

async function getData() {
  try{
    if (storeU.cultureSélectionnée.nom == undefined) {
      notifyError('Aucune culture sélectionnée')
      return
    }

    if (isLoading.value) {
      return
    }
    isLoading.value = true

    storeU.journéeSélectionnée = selectedDay.value.toISOString()

    buckets.value = []
    bucketsInventaire.value = []
    bucketsCommandes.value.clear()

    if (storeU.utilisateur.paramètresSite.vegco != null && storeU.utilisateur.paramètresSite.vegco != undefined) {
      showNotCompletedCommandes.value = storeU.utilisateur.paramètresSite.vegco.afficherCommandesNonLivréesDuPassé
    }

    await storeU.fetchVegcoProduitsIfNeeded()

    const minDate = new Date(selectedDay.value)
    minDate.setDate(minDate.getDate() - 5)
    const maxDate = new Date(selectedDay.value)
    maxDate.setDate(maxDate.getDate() + 7)
    await storeVegco.loadDays(minDate, maxDate)

    const bucketsFound = storeVegco.getStoredBucketsRange(minDate, selectedDay.value)
    bucketsFound.sort((a, b) => {
      if (a.date < b.date) return -1
      if (a.date > b.date) return 1
      return 0
    })
    for (const bucket of bucketsFound) {
      if (bucket.quantité >= unitésParBoîte(bucket.culture) && bucket.commandeVenteId == emptyMongoId() &&
        bucket.semiId != emptyMongoId() && !bucket.semiFermé) {

        // If this bucket id is not already in the list (try fix bug)
        if (buckets.value.find((b) => b.id == bucket.id) != undefined) {
          continue
        }
        buckets.value.push(bucket)
      }
    }

    for (const bucket of storeVegco.bucketsStore) {
      if (bucket.culture == storeU.cultureSélectionnée.nom && bucket.localisation == 'Inventaire' &&
          bucket.commandeVenteId == emptyMongoId() && bucket.semiId == emptyMongoId()) {

        const dateBucket = new Date(bucket.date)
        const dateNow = new Date(selectedDay.value)
        dateNow.setHours(23, 59, 59, 999)
        if (dateNow < dateBucket)
          continue

        if (bucket.quantité == 0 && bucket.quantitéConfirmée == 0) {
          if (storeVegco.getQuantitéPrévision(bucket.codeProduit, bucket.quantitéParPalette, selectedDay.value) == 0) {
            continue
          }
        }

        // If this bucket id is not already in the list (try fix bug)
        if (bucketsInventaire.value.find((b) => b.id == bucket.id) != undefined) {
          continue
        }

        bucketsInventaire.value.push(bucket)
      }
    }

    // Sort inventory buckets by codeProduit
    bucketsInventaire.value.sort((a, b) => {
      if (a.codeProduit < b.codeProduit) return -1
      if (a.codeProduit > b.codeProduit) return 1
      return 0
    })

    for (const commande of storeVegco.commandesStore) {
      addToBucketsCommandes(commande)
    }
    if (storeVegco.commandeInventaire != null) {
      addToBucketsCommandes(storeVegco.commandeInventaire)
    }
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

function filterDétails(commande: MDBVegcoCommandeVente) {
  const filter = commande.détails.filter((détail) => détail.culture == storeU.cultureSélectionnée.nom)
  return filter.map((détail) => {
    return {
      ...détail,
      type: getDétailType(détail.codeProduit),
      quantitéDétail: getQuantitéDétails(commande, détail),
      unités: getUnités(détail.quantité, détail.codeProduit),
      unitésParPalette: getUnitésParPalette(détail.quantitéParPalette, détail.codeProduit),
    }
  })
}

function getDétailType(codeProduit: string) {
  const produitFound = storeU.getVegcoProduit(codeProduit)
  return produitFound?.balise ?? ''
}

function getQuantitéDétails(commande: MDBVegcoCommandeVente, détail: MDBVegcoCommandeVenteDétail) {
  const produit = storeU.getVegcoProduit(détail.codeProduit)
  if (!produit) return ''
  const reste = storeVegco.getResteÀAllouer(commande, détail.codeProduit)
  return `${roundTo((détail.quantité - reste) / produit.quantité, 2)} / ` +
    `${roundTo(détail.quantitéConfirmée / produit.quantité, 2)} / ` +
    `${roundTo(détail.quantité / produit.quantité, 2)}`
}

function getUnitésParPalette(qté: number, codeProduit: string) {
  const produit = storeU.getVegcoProduit(codeProduit)
  if (!produit) return ''
  return roundTo(qté / produit.quantité, 0)
}

function getUnités(qté: number, codeProduit: string) {
  const produit = storeU.getVegcoProduit(codeProduit)
  if (!produit) return ''
  return roundTo(qté / produit.quantité, 2)
}

function onDrag(commandeId: string, args: any) {
  if (args['added'] != undefined) {
    moveBucket(args.added.element, commandeId)
  }
  if (args['removed'] != undefined) {
    // nothing
  }
  if (args['moved'] != undefined) {
    getData()
  }
}

const dialogCulture = useTemplateRef('dialogCulture')
async function showDialogCulture() {
  const result = await dialogCulture.value?.openFew(culturesGérées())
  if (result != undefined && result != '') {
    storeU.sélectionnerCulture(result, null)
    await getData()
  }
}

const dialogBucketAssign = useTemplateRef('dialogBucketAssign')
async function moveBucket(bucket: MDBVegcoBucket, toId: string) {
  let qtéPrévision = undefined as number | undefined
  // Same day or in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selected = new Date(selectedDay.value);
  selected.setHours(0, 0, 0, 0);
  if (selected.getTime() >= today.getTime() &&
    bucket.semiId == emptyMongoId() && bucket.commandeVenteId == emptyMongoId()) {
    qtéPrévision = storeVegco.getQuantitéPrévision(bucket.codeProduit, bucket.quantitéParPalette, selectedDay.value)
  }

  const openResult = dialogBucketAssign.value?.openAssign(bucket, toId, selectedDay.value, qtéPrévision)
  if (openResult === undefined || openResult === false) {
    await getData()
  } else if (openResult instanceof Promise) {
    await openResult;
    await getData()
  }
}

const dialogBucket = useTemplateRef('dialogBucket')
async function openBucket(bucket: MDBVegcoBucket) {
  const modifs = await dialogBucket.value?.openBucketEdit(bucket, selectedDay.value)
  if (modifs)
    getData()
}

const dialogConfirm = useTemplateRef('dialogConfirm')
async function toutPublier() {
  const result = await dialogConfirm.value?.open('Êtes-vous certain de vouloir tout publier les buckets de la page ?');
  if (!result?.valueOf())
    return

  try {
    const config = { params: { publier: true } }
    for (const commande of bucketsCommandes.value.values()) {
      for (const bucket of commande) {
        if (bucket.commandeVenteId != emptyMongoId() && new Date(bucket.datePublication).getFullYear() <= 2000) {
          await apiProphet.put(`vgco/buckets/${bucket.id}/publier`, null, config)
        }
      }
    }
    Notify.success('Tout publié avec succès')
    await getData()
  } catch (error) {
    Notify.error(error)
  }
}

const dialogCommande = useTemplateRef('dialogCommandeEdit')
function editCommande(commande: MDBVegcoCommandeVente) {
  dialogCommande.value?.openCommande(commande)
}

const theme = useTheme()
function getCommandeColor(commande: MDBVegcoCommandeVente) {
  const themeName = theme.global.name.value

  if (commande.statut == 'Annulé') {
    return themeName == 'dark' ? '#201010' : '#FFD0D0'
  }
  else if (commande.statut == 'Inventaire') {
    return themeName == 'dark' ? '#333333' : '#DDDDDD'
  }
  else if (commande.statut == 'Estimé') {
    return themeName == 'dark' ? '#101030' : '#C0C0FF'
  }

  for (const détail of commande.détails) {
    if (détail.culture != storeU.cultureSélectionnée.nom) {
      continue
    }

    const reste = storeVegco.getResteÀAllouer(commande, détail.codeProduit)
    if (reste > 0) {
      // Commande normale qui en reste
      return themeName == 'dark' ? '#222222' : '#FFFFFF'
      // TODO, s'il ne reste pas beaucoup de temps, changer la couleur
    } else if (reste < 0) {
      // Trop d'alloué
      return themeName == 'dark' ? '#401010' : '#FF7070'
    }
  }

  // Commande complète
  return themeName == 'dark' ? '#122912' : '#C0FFC0'
}

function handleScroll(event: any) {
  isFloaty.value = event.target.scrollTop > 144
}

const intervalId = setInterval(async () => {
  if (storeU.cultureSélectionnée.nom == undefined) return
  if (await storeVegco.isRevisionChanged()) getData()
}, 3000)

onMounted(async () => {
  scrollContainer.value = document.querySelector('.v-main__scroller') ||
                          document.querySelector('.v-main') ||
                          document.querySelector('main')
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll)
  }

  await storeU.fetchVegcoProduitsIfNeeded()
  if (storeU.cultureSélectionnée.id == undefined) {
    showDialogCulture()
  } else {
    await getData()
  }
})

onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll)
  }

  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title + culture chip + year -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-truck-trailer" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Commandes</div>
            <div class="text-caption text-medium-emphasis">Planification et livraisons</div>
          </div>
          <v-chip
            color="primary" variant="tonal" size="large" class="ms-1 culture-chip"
            append-icon="mdi-chevron-down" style="cursor: pointer; font-weight: 600;"
            @click="showDialogCulture">
            {{ storeU.cultureSélectionnée.nom ?? 'Culture...' }}
          </v-chip>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <v-checkbox
            v-model="showNotCompletedCommandes"
            label="Afficher les commandes non livrées du passé"
            density="compact" hide-details color="primary"
            @change="onShowNotCompletedCommandesChanged"/>
          <v-btn color="primary" variant="tonal" rounded="pill" prepend-icon="mdi-account-circle" @click="toutPublier">Tout publier</v-btn>
        </div>
      </div>

      <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
        <DaySelector ref="daySelector" v-model="selectedDay"/>
        <span v-if="selectedCodeProduit" class="text-body-2 ms-2">Code produit sélectionné : {{ selectedCodeProduit }}</span>
      </div>
    </div>
    <v-divider/>

    <v-container fluid>
      <v-row>
        <v-col cols="3" lg="2">
          <v-card class="pa-2 sticky-card" :class="{ 'floaty-fixed': isFloaty }">
            <v-card-title>Buckets</v-card-title>
            <v-card-text class="pa-0 pb-0">
              <draggable
                :list="buckets"
                class="draggable-region"
                group="orders" item-key="id" @change="onDrag('', $event)">
                <template #item="{ element }">
                  <BucketCard :bucket="element" :current-date="selectedDay" :selected-code-produit="selectedCodeProduit" @open-bucket="openBucket"/>
                </template>
              </draggable>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3" lg="2">
          <v-card class="pa-2 sticky-card" :class="{ 'floaty-fixed': isFloaty }">
            <v-card-title>Inventaire à jour</v-card-title>
            <v-card-text class="pa-0 pb-0">
              <draggable
                :list="bucketsInventaire"
                class="draggable-region"
                group="orders" item-key="id" @change="onDrag('', $event)">
                <template #item="{ element }">
                  <BucketCard :bucket="element" :current-date="selectedDay" :selected-code-produit="selectedCodeProduit" @open-bucket="openBucket"/>
                </template>
              </draggable>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" lg="8">
          <v-row>
            <v-col v-for="commande in commandesFiltered" :key="commande.id" cols="12" lg="6">
              <v-card :color="getCommandeColor(commande)">
                <span class="position-absolute" style="top: 10px; right: 15px;">
                  <v-btn
                    density="compact" variant="tonal" color="primary"
                    icon="mdi-information" style="opacity: 0.5" @click="editCommande(commande)"/>
                </span>
                <span class="position-absolute" style="top: 10px; right: 35px;">
                  <v-icon
                    v-if="commande.dateCoupéManuelle && new Date(commande.dateCoupéManuelle).getFullYear() > 2000"
                    size="large" color="gray"
                    icon="mdi-exclamation" style="opacity: 0.5"/>
                  <v-tooltip activator="parent" location="bottom">A une date coupé manuelle</v-tooltip>
                </span>
                <v-card-title v-if="commande.statut == 'Inventaire'">Pour inventaire</v-card-title>
                <v-card-title v-else>{{ commande.client }}</v-card-title>
                <v-card-subtitle v-if="commande.statut != 'Inventaire'">No. {{ commande.noCommande }} | {{ commande.statut }} |
                  {{ toDate_wd_dd_MMMM_hh_mm(commande.dateChargé) }}</v-card-subtitle>
                <v-card-text class="pa-0">
                  <v-data-table
                    v-if="commande.statut != 'Inventaire'"
                    :headers="[
                    { title: 'Type', key: 'type', sortable: false },
                    { title: 'Code Produit', key: 'codeProduit', sortable: false },
                    { title: 'Palettes', key: 'nombrePalette', sortable: false },
                    { title: 'Qté', key: 'quantitéDétail', sortable: false },
                    ]"
                    :items="filterDétails(commande)"
                    :items-per-page="-1"
                    class="ml-0 mr-2 my-table"
                    hide-default-footer
                    density="compact">
                    <template #item.codeProduit="{ item }">
                      <v-btn
                        variant="text" :color="selectedCodeProduit == item.codeProduit ? 'green' : 'default'"
                        @click="selectCodeProduit(item.codeProduit)">
                        {{ item.codeProduit }} [{{ item.unitésParPalette }}]
                      </v-btn>
                    </template>
                    <template #item.quantitéDétail="{ item }">
                      <v-tooltip location="top">
                        <template #activator="{ props }">
                          <span v-bind="props">{{ item.quantitéDétail }}</span>
                        </template>
                        Qté assignée à la commande /<br/>
                        Qté envoyée réellement /<br/>
                        Qté de la commande
                      </v-tooltip>
                    </template>
                  </v-data-table>
                  <v-col cols="12" md="12">
                    <draggable
                      group="orders"
                      item-key="id"
                      class="draggable-region"
                      :list="bucketsCommandesProxy.get(commande.id)"
                      @update:list="bucketsCommandesProxy.set(commande.id, $event)"
                      @change="onDrag(commande.id, $event)">
                      <template #item="{ element }">
                      <BucketCard :bucket="element" :current-date="selectedDay" :selected-code-produit="selectedCodeProduit" @open-bucket="openBucket"/>
                    </template>
                    </draggable>
                  </v-col>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
  <DialogCulture ref='dialogCulture'/>
  <DialogBucketAssign ref="dialogBucketAssign"/>
  <DialogBucket ref="dialogBucket"/>
  <DialogConfirm ref="dialogConfirm"/>
  <DialogCommande ref="dialogCommandeEdit"/>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Culture chip hover ───────────────────── */
.culture-chip {
  transition: opacity 0.15s;
}
.culture-chip:hover {
  opacity: 0.78;
}

  .draggable-region {
    min-height: 50px;
    border: 1px dashed #777;
    padding: 4px;
  }

  .draggable-region:empty {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .draggable-region:empty::after {
    content: "Déplacer les buckets ici";
    color: #999;
    font-style: italic;
  }

  :deep(.my-table .v-data-table__th) {
    font-size: 10px;
  }

  .sticky-card {
    overflow: auto;
    max-height: calc(100vh - 280px);
  }

  .floaty-fixed {
    position: fixed;
    top: 80px;
    width: calc(25% - 75px);
    max-height: calc(95vh - 80px);
  }

  @media (min-width: 1280px) {
    .floaty-fixed {
      width: calc(16.666% - 75px);
    }
  }
</style>
